import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Optional header title. */
  title?: ReactNode;
  /** Optional header subtitle / caption under the title. */
  subtitle?: ReactNode;
  /** Optional right-aligned header slot (e.g. a period switch or menu). */
  actions?: ReactNode;
  /** Remove the default body padding (charts manage their own insets). */
  flush?: boolean;
}

/**
 * The base surface for every dashboard panel: theme-aware surface color, a
 * hairline ring, and an optional header row. Body padding is on by default and
 * removed with `flush` so a chart can bleed to the card edges.
 */
export function Card({
  title,
  subtitle,
  actions,
  flush = false,
  className,
  children,
  ...rest
}: CardProps): ReactNode {
  const hasHeader = title !== undefined || subtitle !== undefined || actions !== undefined;
  return (
    <div
      className={cn("rounded-xl border border-hairline bg-surface text-ink shadow-sm", className)}
      {...rest}
    >
      {hasHeader && (
        <div className="flex items-start justify-between gap-3 px-4 pt-4">
          <div className="min-w-0">
            {title !== undefined && (
              <h3 className="truncate text-sm font-semibold text-ink">{title}</h3>
            )}
            {subtitle !== undefined && (
              <p className="mt-0.5 truncate text-xs text-muted">{subtitle}</p>
            )}
          </div>
          {actions !== undefined && <div className="shrink-0">{actions}</div>}
        </div>
      )}
      <div className={cn(!flush && "p-4", hasHeader && !flush && "pt-3")}>{children}</div>
    </div>
  );
}
